import { set } from "lodash-es";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { EventType, DialogType, ConfigState } from "app/types";
import { getParentConfig, getNotifications, getAllNotifications } from "app/api";
import { insideInstalledApp } from "common/utils";
import userIcons from "./user_icons.json";

export const getEnv = () => {
  if (insideInstalledApp()) {
    return "pwa";
  } else if ("ReactNativeWebView" in window) {
    if ("parentsAppPlatform" in window) {
      return window.parentsAppPlatform;
    } else {
      return "webview";
    }
  } else {
    return "web";
  }
};

const initialState: ConfigState = {
  loading: false,
  parentConfig: {
    is_first: true,
    locale: "ja",
    sound: { enable: true, volume: 0.5 },
    bgm: { enable: true, volume: 0.5 },
    project_publish: true,
    project_copied: true,
    ai_chat: true,
    ai_generation: true,
  },
  userConfig: {
    is_first: true,
    locale: "ja",
    sound: { enable: true, volume: 0.5 },
    bgm: { enable: true, volume: 0.5 },
    project_publish: true,
    project_copied: true,
    ai_chat: true,
    ai_generation: true,
  },
  notification: {
    parents: [],
    maintenances: [],
  },
  banned: { words: [] },
  assets: Object.fromEntries(userIcons.map((assets) => [assets.prefix, assets.contents])),
  event: { before_install_prompt: { value: false, args: {} } },
  dialog: {
    loading: { value: false, args: {} },
    notification: { value: false, args: {} },
    error_network: { value: false, args: {} },
    restricted_feature: { value: false, args: {} },
    parent_gate: { value: false, args: {} },
  },
  justAuthenticated: false,
};

const configSlice = createSlice({
  name: "config",
  initialState,
  reducers: {
    setLoading(state: ConfigState, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setJustAuthenticated(state: ConfigState, action: PayloadAction<boolean>) {
      state.justAuthenticated = action.payload;
    },
    updateParentConfig(
      state: ConfigState,
      action: PayloadAction<{ key: string; value: string | boolean | number }>,
    ) {
      const { key, value } = action.payload;
      set(state.parentConfig, key, value);
    },
    updateUserConfig(
      state: ConfigState,
      action: PayloadAction<{ key: string; value: string | boolean | number }>,
    ) {
      const { key, value } = action.payload;
      set(state.userConfig, key, value);
    },
    clearDialog(state: ConfigState) {
      state.dialog = initialState.dialog;
    },
    updateDialog(
      state: ConfigState,
      action: PayloadAction<{
        type: DialogType;
        value: boolean;
        args?: { [key: string]: string };
      }>,
    ) {
      state.dialog[action.payload.type].value = action.payload.value;
      state.dialog[action.payload.type].args = action.payload.args;
    },
    updateEvent(
      state: ConfigState,
      action: PayloadAction<{
        type: EventType;
        value: boolean;
        args?: { [key: string]: string };
      }>,
    ) {
      state.event[action.payload.type].value = action.payload.value;
      state.event[action.payload.type].args = action.payload.args;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getParentConfig.fulfilled, (state: ConfigState, action: PayloadAction<any>) => {
        state.parentConfig = action.payload.user_config;
      })
      .addCase(getNotifications.fulfilled, (state: ConfigState, action: PayloadAction<any>) => {
        state.notification.parents = action.payload.parents;
        state.notification.maintenances = action.payload.maintenances;
      })
      .addCase(getAllNotifications.fulfilled, (state: ConfigState, action: PayloadAction<any>) => {
        state.notification.parents = action.payload.parents;
        state.notification.maintenances = action.payload.maintenances;
      });
  },
});

export const actions = { ...configSlice.actions };

export default configSlice.reducer;
